import { Shift, ShiftStatus, DriverStatus, ComplianceStatus, ComplianceReport, AssignmentStatus } from './types';

export const getStatusColor = (status: ShiftStatus | DriverStatus | AssignmentStatus) => {
  switch (status) {
    case 'active':
    case 'Online':
    case 'confirmed':
    case 'checked_in':
      return 'bg-green-50/80 text-green-700 border border-green-200/60';
    case 'draft':
    case 'pending':
    case 'Idle':
      return 'bg-blue-50/80 text-blue-700 border border-blue-200/60';
    case 'Busy':
      return 'bg-amber-50/80 text-amber-700 border border-amber-200/60';
    case 'completed':
      return 'bg-gray-50/80 text-gray-600 border border-gray-200/60';
    case 'cancelled':
    case 'no_show':
      return 'bg-red-50/80 text-red-700 border border-red-200/60';
    default:
      return 'bg-gray-50/80 text-gray-600 border border-gray-200/60';
  }
};

export const getShiftDurationHours = (shift: Shift): number => {
  const parseTime = (timeStr: string): number => {
    if (timeStr.includes('T') || timeStr.includes('-')) {
      const date = new Date(timeStr);
      if (!isNaN(date.getTime())) {
        return date.getHours() + date.getMinutes() / 60;
      }
    }
    
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours + minutes / 60;
  };

  const parseDateTime = (dateTimeStr: string): Date | null => {
    const date = new Date(dateTimeStr);
    if (!isNaN(date.getTime())) {
      return date;
    }
    return null;
  };

  const startDate = parseDateTime(shift.startTime);
  const endDate = parseDateTime(shift.endTime);
  
  if (startDate && endDate) {
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours < 0 ? diffHours + 24 : diffHours;
  }

  let start = parseTime(shift.startTime);
  let end = parseTime(shift.endTime);

  if (end < start) {
    end += 24;
  }

  return end - start;
};

export const calculateComplianceStatus = (
  report: ComplianceReport,
  shift: Shift
): ComplianceStatus => {
  const shiftDurationHours = getShiftDurationHours(shift);
  const shiftDurationSeconds = shiftDurationHours * 3600;
  const requiredOnlineSeconds = shiftDurationSeconds * 0.95;

  let onlineDurationSeconds = 0;
  if (report.onlineSeconds !== undefined) {
    onlineDurationSeconds = report.onlineSeconds;
  } else {
    onlineDurationSeconds = report.onlineDurationHours * 3600;
  }

  if (onlineDurationSeconds < requiredOnlineSeconds) {
    return 'Non-Compliant';
  }

  if (report.acceptanceRate < 90) {
    return 'Non-Compliant';
  }

  return 'Compliant';
};

export const getOnlineDurationHours = (onlineSeconds: number): number => {
  return onlineSeconds / 3600;
};

export const calculateAcceptanceRate = (
  ordersAccepted: number,
  ordersRejected: number
): number => {
  const total = ordersAccepted + ordersRejected;
  if (total === 0) return 100;
  return Math.round((ordersAccepted / total) * 100);
};

export const formatDateTime = (dateTimeStr: string | undefined | null): string => {
  if (!dateTimeStr) return 'N/A';
  try {
    if (dateTimeStr.includes('T')) {
      const dateTimeMatch = dateTimeStr.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (dateTimeMatch) {
        const [, year, month, day, hours, minutes] = dateTimeMatch;
        const yearNum = parseInt(year, 10);
        const monthNum = parseInt(month, 10) - 1; // Month is 0-indexed
        const dayNum = parseInt(day, 10);
        const hoursNum = parseInt(hours, 10);
        const minutesNum = parseInt(minutes, 10);
        
        const date = new Date(yearNum, monthNum, dayNum, hoursNum, minutesNum, 0, 0);
        return date.toLocaleString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric', 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true
        });
      }
    }
    
    // Fallback to Date parsing (with timezone conversion)
    const date = new Date(dateTimeStr);
    if (isNaN(date.getTime())) return dateTimeStr;
    
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return dateTimeStr;
  }
};

export const formatTime = (dateTimeStr: string | undefined | null): string => {
  if (!dateTimeStr) return 'N/A';
  try {
    if (/^\d{1,2}:\d{2}$/.test(dateTimeStr)) {
      const [hours, minutes] = dateTimeStr.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true
      });
    }
    
    if (dateTimeStr.includes('T')) {
      const timeMatch = dateTimeStr.match(/T(\d{2}):(\d{2}):(\d{2})/);
      if (timeMatch) {
        const [, hours, minutes] = timeMatch;
        const hoursNum = parseInt(hours, 10);
        const minutesNum = parseInt(minutes, 10);
        const date = new Date();
        date.setHours(hoursNum, minutesNum, 0, 0);
        return date.toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true
        });
      }
    }
    
    const date = new Date(dateTimeStr);
    if (isNaN(date.getTime())) return dateTimeStr;
    
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return dateTimeStr;
  }
};

export const formatDate = (dateTimeStr: string | undefined | null): string => {
  if (!dateTimeStr) return 'N/A';
  try {
    const date = new Date(dateTimeStr);
    if (isNaN(date.getTime())) return dateTimeStr;
    
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  } catch {
    return dateTimeStr;
  }
};

// Convert YYYY-MM-DD (ISO format) to dd/mm/yy
export const formatDateToDDMMYY = (isoDate: string | undefined | null): string => {
  if (!isoDate) return '';
  try {
    // Handle YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
      const [year, month, day] = isoDate.split('-');
      const yearShort = year.slice(-2);
      return `${day}/${month}/${yearShort}`;
    }
    // If already in dd/mm/yy format, return as is
    if (/^\d{2}\/\d{2}\/\d{2}$/.test(isoDate)) {
      return isoDate;
    }
    // Try parsing as Date
    const date = new Date(isoDate);
    if (!isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    }
    return isoDate;
  } catch {
    return isoDate || '';
  }
};

// Convert dd/mm/yy to YYYY-MM-DD (ISO format)
export const formatDateFromDDMMYY = (ddmmyy: string | undefined | null): string => {
  if (!ddmmyy) return '';
  try {
    // Handle dd/mm/yy format
    if (/^\d{2}\/\d{2}\/\d{2}$/.test(ddmmyy)) {
      const [day, month, year] = ddmmyy.split('/');
      // Convert 2-digit year to 4-digit (assuming 20xx for years 00-99)
      const fullYear = parseInt(year, 10) < 50 ? `20${year}` : `19${year}`;
      return `${fullYear}-${month}-${day}`;
    }
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(ddmmyy)) {
      return ddmmyy;
    }
    return ddmmyy;
  } catch {
    return ddmmyy || '';
  }
};

// Validate dd/mm/yy format
export const isValidDDMMYY = (dateStr: string): boolean => {
  if (!dateStr) return false;
  const pattern = /^\d{2}\/\d{2}\/\d{2}$/;
  if (!pattern.test(dateStr)) return false;
  
  const [day, month, year] = dateStr.split('/').map(Number);
  const fullYear = year < 50 ? 2000 + year : 1900 + year;
  
  // Basic validation
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  
  // Check if date is valid
  const date = new Date(fullYear, month - 1, day);
  return date.getFullYear() === fullYear && 
         date.getMonth() === month - 1 && 
         date.getDate() === day;
};

// Check if current time is within the shift's active window (between startTime and endTime)
export const isWithinShiftTimeWindow = (shift: Shift): boolean => {
  if (!shift.startTime || !shift.endTime) return false;
  
  try {
    const now = new Date();
    const startTime = new Date(shift.startTime);
    const endTime = new Date(shift.endTime);
    
    if (isNaN(startTime.getTime()) || isNaN(endTime.getTime())) {
      return false;
    }
    
    return now >= startTime && now <= endTime;
  } catch {
    return false;
  }
};

